import { useCallback, useEffect, useState } from 'react';
import { requestBrowserCoordinates } from '../hrms/api/attendance';

export type AttendanceLocationStatus =
  | 'not-required'
  | 'checking'
  | 'prompt'
  | 'granted'
  | 'denied'
  | 'unavailable';

export interface AttendanceLocationAccess {
  status: AttendanceLocationStatus;
  error: string | null;
  requestAccess: () => Promise<boolean>;
}

const unsupportedMessage = "Geolocation isn't supported by this browser.";
const deniedSettingsMessage =
  'Location access is blocked. Allow location in your browser settings, then try again.';

const isPermissionError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error || '');
  return /permission denied|location access is blocked/i.test(message);
};

const errorMessage = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : 'Could not get your location.';

export function useAttendanceLocationAccess(
  required: boolean,
): AttendanceLocationAccess {
  const [status, setStatus] = useState<AttendanceLocationStatus>(
    required ? 'checking' : 'not-required',
  );
  const [error, setError] = useState<string | null>(null);

  const requestAccess = useCallback(async (): Promise<boolean> => {
    if (!required) {
      setStatus('not-required');
      setError(null);
      return true;
    }

    if (
      typeof navigator === 'undefined' ||
      !navigator.geolocation
    ) {
      setStatus('unavailable');
      setError(unsupportedMessage);
      return false;
    }

    setStatus('checking');
    setError(null);
    try {
      await requestBrowserCoordinates();
      setStatus('granted');
      return true;
    } catch (requestError) {
      setStatus(isPermissionError(requestError) ? 'denied' : 'unavailable');
      setError(errorMessage(requestError));
      return false;
    }
  }, [required]);

  useEffect(() => {
    let cancelled = false;

    if (!required) {
      setStatus('not-required');
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    if (
      typeof navigator === 'undefined' ||
      !navigator.geolocation
    ) {
      setStatus('unavailable');
      setError(unsupportedMessage);
      return () => {
        cancelled = true;
      };
    }

    const permissions = navigator.permissions;
    if (!permissions?.query) {
      setStatus('prompt');
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setStatus('checking');
    setError(null);
    permissions
      .query({ name: 'geolocation' })
      .then(async (permission) => {
        if (cancelled) return;
        if (permission.state === 'denied') {
          setStatus('denied');
          setError(deniedSettingsMessage);
          return;
        }
        if (permission.state === 'prompt') {
          setStatus('prompt');
          return;
        }
        try {
          await requestBrowserCoordinates();
          if (!cancelled) setStatus('granted');
        } catch (requestError) {
          if (cancelled) return;
          setStatus(isPermissionError(requestError) ? 'denied' : 'unavailable');
          setError(errorMessage(requestError));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('prompt');
          setError(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [required]);

  return { status, error, requestAccess };
}
